import React, { useState } from 'react';
import { Item } from '@/stores/canvasStore';
import { FileText, Maximize2, Eye, Download, File, X } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cn } from '@/lib/utils';

interface PdfCardProps {
  item: Item;
}

interface PdfContent {
  url?: string;
  filename?: string;
  mimeType?: string;
  size?: number;
  storagePath?: string;
  pageCount?: number;
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

export function PdfCard({ item }: PdfCardProps) {
  const [isExpandedModal, setIsExpandedModal] = useState(false);
  const [showControls, setShowControls] = useState(false);

  const content = item.content as PdfContent;
  const { url, filename, size } = content;
  const displayName = filename || 'Document.pdf';
  const displaySize = size ? formatFileSize(size) : '';

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsExpandedModal(true);
  };

  const handleViewClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsExpandedModal(true);
  };

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (url) {
      window.open(url, '_blank');
    }
  };

  return (
    <>
      <div
        className="w-full h-full rounded-lg overflow-hidden card-shadow bg-card relative group"
        onMouseEnter={() => setShowControls(true)}
        onMouseLeave={() => setShowControls(false)}
        title=""
      >
        {/* PDF preview area — styled document look */}
        <div className="absolute inset-0 bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-950/30 dark:to-orange-950/20 flex flex-col items-center justify-center">
          {/* Decorative page lines */}
          <div className="absolute inset-x-6 top-6 bottom-12 bg-white/60 dark:bg-white/5 rounded-md border border-gray-200/50 dark:border-gray-700/30 flex flex-col p-4 gap-2 overflow-hidden">
            <div className="h-2 w-3/4 bg-gray-200/70 dark:bg-gray-600/30 rounded" />
            <div className="h-2 w-full bg-gray-200/50 dark:bg-gray-600/20 rounded" />
            <div className="h-2 w-5/6 bg-gray-200/50 dark:bg-gray-600/20 rounded" />
            <div className="h-2 w-full bg-gray-200/40 dark:bg-gray-600/15 rounded" />
            <div className="h-2 w-2/3 bg-gray-200/40 dark:bg-gray-600/15 rounded" />
            <div className="h-2 w-full bg-gray-200/30 dark:bg-gray-600/10 rounded" />
            <div className="h-2 w-4/5 bg-gray-200/30 dark:bg-gray-600/10 rounded" />
          </div>

          {/* PDF badge */}
          <div className="absolute top-3 left-3 bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider">
            PDF
          </div>
        </div>

        {/* View button overlay (like VideoCard's play button) */}
        <div
          className={cn(
            "absolute inset-0 flex items-center justify-center transition-colors cursor-pointer",
            "bg-black/10 group-hover:bg-black/25"
          )}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={handleViewClick}
          title=""
        >
          <div
            className={cn(
              "w-14 h-14 rounded-full bg-white flex items-center justify-center shadow-lg transition-all",
              "group-hover:scale-110"
            )}
          >
            <Eye className="w-6 h-6 text-slate-900" />
          </div>
        </div>

        {/* Top-right controls: Expand and Download */}
        <div
          className={cn(
            "absolute top-2 right-2 flex gap-1.5 transition-opacity",
            showControls ? "opacity-100" : "opacity-0"
          )}
        >
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={handleDownload}
            className="w-8 h-8 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center text-white transition-colors"
            title=""
          >
            <Download className="w-4 h-4" />
          </button>
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={handleExpandClick}
            className="w-8 h-8 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center text-white transition-colors"
            title=""
          >
            <Maximize2 className="w-4 h-4" />
          </button>
        </div>

        {/* Bottom info bar (like VideoCard's info bar) */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
          <div className="flex items-center justify-between text-white text-xs">
            <span className="truncate flex-1 mr-2" title="">{displayName}</span>
            <div className="flex items-center gap-2">
              {displaySize && (
                <span className="flex items-center gap-1 whitespace-nowrap">
                  <File className="w-3 h-3" />
                  {displaySize}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Expanded PDF viewer modal */}
      <Dialog open={isExpandedModal} onOpenChange={setIsExpandedModal}>
        <DialogContent
          className="max-w-5xl h-[85vh] p-0 bg-background border overflow-hidden flex flex-col [&>button:last-child]:hidden"
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <DialogTitle className="sr-only">Viewing: {displayName}</DialogTitle>
          {/* Header bar — close + download buttons live here to avoid overlap */}
          <div className="flex items-center justify-between px-4 py-2 border-b bg-muted/30 flex-shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              <FileText className="w-4 h-4 text-red-500 flex-shrink-0" />
              <span className="text-sm font-medium truncate">{displayName}</span>
              {displaySize && (
                <span className="text-xs text-muted-foreground flex-shrink-0">({displaySize})</span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={handleDownload}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded-md hover:bg-muted"
                onMouseDown={(e) => e.stopPropagation()}
              >
                <Download className="w-3.5 h-3.5" />
                Download
              </button>
              <DialogPrimitive.Close className="rounded-sm p-1 opacity-70 hover:opacity-100 transition-opacity">
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </DialogPrimitive.Close>
            </div>
          </div>
          {/* PDF viewer — iframe with fallback for browsers that block inline PDFs */}
          {url && (
            <div className="flex-1 w-full min-h-0 relative">
              <iframe
                src={url}
                className="w-full h-full border-0"
                title={displayName}
                onMouseDown={(e) => e.stopPropagation()}
              />
              {/* Fallback: if the browser blocks the iframe, show a download prompt */}
              <noscript>
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-muted/50">
                  <p className="text-sm text-muted-foreground mb-2">PDF preview unavailable.</p>
                  <button onClick={handleDownload} className="text-primary underline text-sm">Download PDF</button>
                </div>
              </noscript>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
